import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// Pre-construction step 2, Pick bidders (the prototype's step 2 and its
// pop-ups). Every package's pane is on the page, so switching packages is
// instant (precon#selectPackage just shows another pane). Ticks, Pick all /
// Clear and the self-perform switch change the page first; the server
// confirms in one request and answers with the summary and the steps to
// reload quietly (`stale`). On failure the change is rolled back with an
// error toast. Also: the resizable split bar, Our own bid, Note for a trade,
// Find subs near this job (+ details panel) and Import your sub list.
// Lives on the step 2 card, or on one pane in the By cost code view.
export default class extends Controller {
  static targets = ["list", "bar", "stat", "foot", "strings",
                    "seModal", "seTitle", "seRows", "seTotal", "seVs", "seRowTpl",
                    "tnModal", "tnTitle", "tnNote", "tnLines", "tnLineTpl", "tnEvery", "tnRemove", "tnSave",
                    "findModal", "findSub", "findSearch", "findCodes", "findSrc", "findBusy", "findCodeLabel", "findLines",
                    "findResults", "findFound", "findFilter", "findRows", "findFoot", "findAddList", "findAddPick", "fpanel",
                    "impModal", "impDrop", "impFile", "impChip", "impMapTtl", "impMap", "impNote", "impBtn"]
  static values = { baseUrl: String, projectId: Number, auto: Boolean }

  connect() {
    try { this.S = JSON.parse(this.hasStringsTarget ? this.stringsTarget.textContent : "{}") } catch (e) { this.S = {} }
    this.S.js = this.S.js || {}
    this._applySavedWidth()
    this._onKey = (e) => { if (e.key === "Escape") this._closeAll() }
    document.addEventListener("keydown", this._onKey)
    // AI picks on its own — but only once step 2 is actually on screen (the
    // page also loads step 2 in the background, and that must not start AI).
    this._onStepShown = (e) => { if (e.detail?.step === 2) this._autoPick() }
    document.addEventListener("precon:step-shown", this._onStepShown)
    if (this._visible()) this._autoPick()
  }

  _visible() {
    const section = this.element.closest("section[data-step]")
    return !section || section.style.display !== "none"
  }

  _autoPick() {
    if (!this.autoValue || this.element.dataset.autoAsked) return
    this.element.dataset.autoAsked = "1"
    this._json("POST", `${this.baseUrlValue}/bidders/ai`, { auto: "1" })
      .then(() => this._precon()?._applyResponse({ stale: [2] }))
      .catch(() => {})
  }

  disconnect() {
    document.removeEventListener("precon:step-shown", this._onStepShown)
    document.removeEventListener("keydown", this._onKey)
    if (this._findTimer) clearInterval(this._findTimer)
  }

  // ── Small helpers ─────────────────────────────────────────────────────
  _precon() {
    const el = this.element.closest("[data-controller~='precon']")
    return el ? this.application.getControllerForElementAndIdentifier(el, "precon") : null
  }

  _t(key, vars = {}) {
    let s = this.S.js[key]
    if (s && typeof s === "object") s = (vars.count === 1 && s.one) ? s.one : (s.other || s.one || key)
    s = s || key
    Object.entries(vars).forEach(([k, v]) => { s = s.split(`%{${k}}`).join(v) })
    return s
  }

  _toast(msg, type = "notice") {
    const p = this._precon()
    if (p && p._showToast) { p._showToast(msg, type); return }
    const t = document.createElement("div")
    t.className = type === "error" ? "flash-toast-alert" : "flash-toast-notice"
    t.setAttribute("data-controller", "flash")
    t.setAttribute("data-flash-dismiss-after-value", "5000")
    t.textContent = msg
    document.body.prepend(t)
  }

  _money(n) { return "$" + Math.round(Number(n) || 0).toLocaleString("en-US") }

  _num(v) { const n = parseFloat(String(v ?? "").replace(/[^\d.\-]/g, "")); return isNaN(n) ? 0 : n }

  _esc(s) { return String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])) }

  async _json(method, url, body) {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const res = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token },
      body: body === undefined ? undefined : JSON.stringify(body)
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || data.ok === false) throw new Error(data.error || this._t("error"))
    return data
  }

  _pane(id) { return this.element.querySelector(`[data-precon-target='packagePanel'][data-package-id='${id}']`) || (this.element.dataset.packageId === String(id) ? this.element : null) }

  _paneData(id) { try { return JSON.parse(this._pane(id)?.dataset.pane || "{}") } catch (e) { return {} } }

  // The package on screen: the visible pane (or the only one).
  _currentPackageId() {
    const panes = [...this.element.querySelectorAll("[data-precon-target='packagePanel']")]
    if (!panes.length && this.element.dataset.packageId) return this.element.dataset.packageId
    return (panes.find(p => !p.classList.contains("hidden")) || panes[0])?.dataset.packageId
  }

  stop(e) { e.stopPropagation() }

  goSubs(e) { window.location.href = e.currentTarget.dataset.url }

  _closeAll() {
    if (this.hasSeModalTarget && !this.seModalTarget.classList.contains("hidden")) this.closeOurBid()
    ;["tnModal", "findModal", "impModal"].forEach(t => { if (this[`has${t[0].toUpperCase()}${t.slice(1)}Target`]) this[`${t}Target`].classList.add("hidden") })
  }

  // ── Ticks, Pick all, Clear (optimistic, one request) ──────────────────
  _boxes(pkgId) {
    const pane = this._pane(pkgId)
    return pane ? [...pane.querySelectorAll(".t2:not(.head):not(.selfrow) input[type=checkbox][data-sub-id]")] : []
  }

  _paint(pkgId) {
    const boxes = this._boxes(pkgId)
    boxes.forEach(b => b.closest(".t2").classList.toggle("dim", !b.checked))
    const on = boxes.filter(b => b.checked).length
    const stat = this.statTargets.find(s => s.dataset.packageId === String(pkgId))
    if (stat) {
      stat.textContent = boxes.length ? this._t("n_of", { on, all: boxes.length }) : (stat.dataset.none || stat.textContent)
      stat.classList.toggle("muted", on > 0)
      stat.classList.toggle("warn", on === 0)
    }
    if (this.hasFootTarget) {
      const picked = this.element.querySelectorAll(".t2:not(.head):not(.selfrow) input[type=checkbox][data-sub-id]:checked").length
      const count = this.element.querySelectorAll("[data-precon-target='packageRow']").length
      this.footTarget.textContent = this._t("foot", { picked, count })
    }
  }

  async _send(pkgId, subIds, picked, boxes, okMsg) {
    const before = boxes.map(b => b.checked)
    boxes.forEach(b => { b.checked = picked })
    this._paint(pkgId)
    try {
      const data = await this._json("POST", `${this.baseUrlValue}/bidders/pick`, { package_id: pkgId, sub_ids: subIds, picked: picked ? "1" : "0" })
      // The server's word wins (someone already invited stays ticked).
      const on = new Set((data.picked_sub_ids || []).map(String))
      this._boxes(pkgId).forEach(b => { b.checked = on.has(b.dataset.subId) })
      this._paint(pkgId)
      this._precon()?._applyResponse({ summary: data.summary, stale: data.stale })
      if (okMsg) this._toast(okMsg)
    } catch (err) {
      boxes.forEach((b, i) => { b.checked = before[i] })
      this._paint(pkgId)
      this._toast(err.message, "error")
    }
  }

  toggle(e) {
    const cb = e.currentTarget
    const pkgId = cb.dataset.packageId
    const want = cb.checked
    // Already invited: they stay (they decline from their own bid page).
    if (!want && cb.dataset.invited === "1") { cb.checked = true; this._toast(this.S.alreadyInvited || this._t("error"), "error"); return }
    cb.checked = !want // _send sets it (and rolls back)
    this._send(pkgId, [cb.dataset.subId], want, [cb])
  }

  pickAll(e) {
    const pkgId = e.currentTarget.dataset.packageId
    const boxes = this._boxes(pkgId).filter(b => !b.checked)
    if (!this._boxes(pkgId).length) { this._toast(this._t("nobody")); return }
    if (!boxes.length) { this._toast(this._t("all_picked")); return }
    this._send(pkgId, boxes.map(b => b.dataset.subId), true, boxes, this._t("all_picked"))
  }

  clearAll(e) {
    const pkgId = e.currentTarget.dataset.packageId
    const boxes = this._boxes(pkgId).filter(b => b.dataset.invited !== "1" && b.checked)
    if (!boxes.length) { this._toast(this._t("cleared")); return }
    this._send(pkgId, boxes.map(b => b.dataset.subId), false, boxes, this._t("cleared"))
  }

  // ── "Add <company>": self-perform on this package ─────────────────────
  _setSelf(pkgId, on) {
    const pane = this._pane(pkgId)
    if (!pane) return
    pane.querySelectorAll(`input[data-action*='toggleSelf']`).forEach(i => { i.checked = on })
    pane.querySelector(".selfrow")?.classList.toggle("hidden", !on)
  }

  async toggleSelf(e) {
    const pkgId = e.currentTarget.dataset.packageId
    const on = e.currentTarget.checked
    this._setSelf(pkgId, on)
    const d = this._paneData(pkgId)
    if (on && !this._linesTotal(d.selfLines || [])) this._openOurBid(pkgId)
    try {
      const data = await this._json("POST", `${this.baseUrlValue}/bidders/self_perform`, { package_id: pkgId, on: on ? "1" : "0" })
      this._precon()?._applyResponse({ summary: data.summary, stale: data.stale })
    } catch (err) {
      this._setSelf(pkgId, !on)
      this._toast(err.message, "error")
    }
  }

  // ── Our own bid ───────────────────────────────────────────────────────
  _linesTotal(lines) { return lines.reduce((a, l) => a + this._num(l.q) * this._num(l.c), 0) }

  openOurBid(e) { this._openOurBid(e.currentTarget.dataset.packageId) }

  _openOurBid(pkgId) {
    const d = this._paneData(pkgId)
    const codes = d.codes || []
    let lines = (d.selfLines || []).map(l => ({ ...l }))
    if (!lines.length) lines = codes.map(c => ({ d: c.label.replace(/^\S+ \S+ \S+ /, ""), q: "1", u: "LS", c: "0", code: c.code }))
    this._se = { pkgId, codes, lines, lowBid: d.lowBid, dirty: false }
    this.seTitleTarget.textContent = (this.S.seTitle || "%{label}").replace("%{label}", d.label)
    this._renderSe()
    this.seModalTarget.classList.remove("hidden")
    track("Precon Own Bid Opened", { project_id: this.projectIdValue, package_id: pkgId })
  }

  _renderSe() {
    const se = this._se
    this.seRowsTarget.innerHTML = ""
    se.lines.forEach((l, i) => {
      const row = this.seRowTplTarget.content.firstElementChild.cloneNode(true)
      const f = (k) => row.querySelector(`[data-f='${k}']`)
      f("d").value = l.d || ""; f("q").value = l.q || ""; f("c").value = l.c || ""
      f("u").textContent = l.u || "LS"
      const code = se.codes.find(c => c.code === l.code) || se.codes[0]
      f("meta").textContent = code ? this._t("goes_in", { label: code.label }) : ""
      ;["d", "q", "c"].forEach(k => f(k).addEventListener("input", (ev) => { l[k] = ev.target.value; se.dirty = true; this._seTotals() }))
      f("x").addEventListener("click", () => { se.lines.splice(i, 1); se.dirty = true; this._renderSe() })
      this.seRowsTarget.appendChild(row)
    })
    this._seTotals()
  }

  _seTotals() {
    const se = this._se
    se.lines.forEach((l, i) => { const t = this.seRowsTarget.children[i]?.querySelector("[data-f='tot']"); if (t) t.textContent = this._money(this._num(l.q) * this._num(l.c)) })
    const tot = this._linesTotal(se.lines)
    this.seTotalTarget.textContent = this._money(tot)
    const low = se.lowBid
    if (low && tot) {
      const below = tot <= low.amount
      this.seVsTarget.className = below ? "up" : "down"
      this.seVsTarget.textContent = this._t(below ? "vs_below" : "vs_above", { amount: this._money(Math.abs(low.amount - tot)), name: low.name })
    } else { this.seVsTarget.className = ""; this.seVsTarget.textContent = "" }
  }

  addSeLine() {
    const se = this._se
    se.lines.push({ d: this._t("new_line"), q: "1", u: "LS", c: "0", code: se.codes[0]?.code || "" })
    se.dirty = true
    this._renderSe()
  }

  // Done, ×, the backdrop and Escape all keep what was typed (like the
  // prototype). The self-perform row shows the new number at once.
  _paintSelf(pkgId, lines) {
    const pane = this._pane(pkgId)
    if (!pane) return
    const d = this._paneData(pkgId); d.selfLines = lines; pane.dataset.pane = JSON.stringify(d)
    const total = this._linesTotal(lines)
    const sub = pane.querySelector("[data-precon-pick-target='selfSub']")
    const btn = pane.querySelector("[data-precon-pick-target='selfPrice']")
    if (sub) sub.textContent = total > 0 ? (this.S.selfPriced || "%{amount}").replace("%{amount}", this._money(total)) : (this.S.selfUnpriced || "")
    if (btn) btn.textContent = total > 0 ? (this.S.selfEdit || btn.textContent) : (this.S.selfPrice || btn.textContent)
  }

  async closeOurBid() {
    this.seModalTarget.classList.add("hidden")
    const se = this._se
    if (!se || !se.dirty) return
    se.dirty = false
    const before = (this._paneData(se.pkgId).selfLines || []).map(l => ({ ...l }))
    const lines = se.lines.map(l => ({ ...l }))
    this._paintSelf(se.pkgId, lines)
    try {
      const data = await this._json("PATCH", `${this.baseUrlValue}/bidders/our_bid`, { package_id: se.pkgId, lines })
      this._precon()?._applyResponse({ summary: data.summary, stale: data.stale })
      this._toast(this._t("bid_saved"))
    } catch (err) {
      this._paintSelf(se.pkgId, before)
      this._toast(err.message, "error")
    }
  }

  // ── Note for a trade ──────────────────────────────────────────────────
  _noteEl(pkgId) { return this._pane(pkgId)?.querySelector("[data-precon-pick-target='note']") }

  openNote(e) {
    const pkgId = e.currentTarget.dataset.packageId
    const n = JSON.parse(this._noteEl(pkgId)?.dataset.note || "{}")
    const d = this._paneData(pkgId)
    this._tn = { pkgId, code: n.code, saved: !!n.saved, wasEvery: !!(n.saved && n.every), label: d.label }
    this.tnTitleTarget.textContent = (this.S.tnTitle || "%{label}").replace("%{label}", d.label)
    this.tnNoteTarget.value = n.note || ""
    this.tnLinesTarget.innerHTML = ""
    ;(n.lines && n.lines.length ? n.lines : [""]).forEach(v => this._addNoteLine(v))
    this.tnEveryTarget.checked = n.saved ? !!n.every : true
    this.tnRemoveTarget.classList.toggle("hidden", !n.saved)
    this.noteChanged()
    this.tnModalTarget.classList.remove("hidden")
  }

  _addNoteLine(v = "") {
    const row = this.tnLineTplTarget.content.firstElementChild.cloneNode(true)
    row.querySelector("input").value = v
    this.tnLinesTarget.appendChild(row)
    return row
  }

  addNoteLine() { this._addNoteLine("").querySelector("input").focus() }

  removeNoteLine(e) { e.currentTarget.closest(".tnlrow").remove(); this.noteChanged() }

  noteChanged() {
    const any = this.tnNoteTarget.value.trim() || [...this.tnLinesTarget.querySelectorAll("input")].some(i => i.value.trim())
    this.tnSaveTarget.disabled = !any
  }

  closeNote() { this.tnModalTarget.classList.add("hidden") }

  _replaceNote(pkgId, html) {
    const el = this._noteEl(pkgId)
    if (!el || !html) return
    const tmp = document.createElement("div")
    tmp.innerHTML = html.trim()
    if (tmp.firstElementChild) el.replaceWith(tmp.firstElementChild)
  }

  // The note row / "+ Add a note" right away; the server's copy replaces it.
  _paintNote(pkgId, n) {
    const el = this._noteEl(pkgId)
    if (!el) return
    const label = this._paneData(pkgId).label || ""
    el.dataset.note = JSON.stringify(n)
    const act = `data-action="click->precon-pick#openNote" data-package-id="${this._esc(pkgId)}"`
    el.innerHTML = n.saved
      ? `<div class="tnrow"><div class="grow"><div><b>${this._esc(this.S.noteHead)}</b>${n.every ? `<span class="badge b-gray tnev">${this._esc(this.S.everyJob)}</span>` : ""}</div><div class="muted">${this._esc(n.note)}</div>${n.lines.length ? `<div class="tnl">${this._esc((this.S.priceSep || "%{lines}").replace("%{lines}", n.lines.join(" · ")))}</div>` : ""}</div><button class="linkbtn" type="button" ${act}>${this._esc(this.S.edit)}</button></div>`
      : `<div class="tnadd"><button class="btn btn-secondary btn-sm" type="button" ${act}>${this._esc(this.S.noteAdd)}</button><span class="muted">${this._esc((this.S.noteAddHint || "").replace("%{label}", label))}</span></div>`
  }

  async saveNote() {
    const tn = this._tn
    const lines = [...this.tnLinesTarget.querySelectorAll("input")].map(i => i.value.trim()).filter(Boolean)
    const every = this.tnEveryTarget.checked
    const note = this.tnNoteTarget.value.trim()
    const before = this._noteEl(tn.pkgId)?.dataset.note
    this.closeNote()
    this._paintNote(tn.pkgId, { code: tn.code, note, lines, every, saved: true })
    try {
      const data = await this._json("POST", `${this.baseUrlValue}/bidders/trade_note`, { package_id: tn.pkgId, code: tn.code, note, lines, every: every ? "1" : "0", was_every: tn.wasEvery ? "1" : "0" })
      this._replaceNote(tn.pkgId, data.html)
      this._precon()?._applyResponse({ stale: data.stale })
      this._toast(this._t(every ? "note_saved_every" : "note_saved", { label: tn.label }))
    } catch (err) {
      if (before) this._paintNote(tn.pkgId, JSON.parse(before))
      this._toast(err.message, "error")
    }
  }

  async removeNote() {
    const tn = this._tn
    const before = this._noteEl(tn.pkgId)?.dataset.note
    this.closeNote()
    this._paintNote(tn.pkgId, { code: tn.code, note: "", lines: [], every: true, saved: false })
    try {
      const data = await this._json("DELETE", `${this.baseUrlValue}/bidders/trade_note`, { package_id: tn.pkgId, code: tn.code, every: tn.wasEvery ? "1" : "0" })
      this._replaceNote(tn.pkgId, data.html)
      this._precon()?._applyResponse({ stale: data.stale })
      this._toast(this._t("note_removed"))
    } catch (err) {
      if (before) this._paintNote(tn.pkgId, JSON.parse(before))
      this._toast(err.message, "error")
    }
  }

  // ── Find subs near this job ───────────────────────────────────────────
  openFind(e) {
    const pkgId = e?.currentTarget?.dataset.packageId || this._currentPackageId()
    if (!pkgId) return
    this._find = { pkgId, origin: pkgId, rows: [], sel: {}, strong: false, detail: null }
    this._renderFindCodes()
    this._findStep("search")
    this.findModalTarget.classList.remove("hidden")
    track("Precon Find Subs Opened", { project_id: this.projectIdValue, package_id: pkgId })
  }

  closeFind() {
    this.findModalTarget.classList.add("hidden")
    if (this._findTimer) clearInterval(this._findTimer)
    this.closeDetail()
  }

  _findStep(step) {
    this.findSearchTarget.classList.toggle("hidden", step !== "search")
    this.findBusyTarget.classList.toggle("hidden", step !== "busy")
    this.findResultsTarget.classList.toggle("hidden", step !== "results")
    this.findSubTarget.textContent = step === "results" ? this.findSubTarget.dataset.results : this.findSubTarget.dataset.search
    if (step !== "results") this.closeDetail()
  }

  _pkgInfo(id) { return (this.S.packages || []).find(p => String(p.id) === String(id)) || { id, label: this._paneData(id).label || "" } }

  _renderFindCodes() {
    const f = this._find
    const origin = this._pkgInfo(f.origin)
    const thin = (this.S.packages || []).filter(p => p.thin && String(p.id) !== String(f.origin)).slice(0, 5)
    const chips = [origin, ...thin]
    this.findCodesTarget.innerHTML = chips.map(p => `<button class="fchip ${String(p.id) === String(f.pkgId) ? "on" : ""}" type="button" data-id="${p.id}">${this._esc(p.label)}</button>`).join("")
    this.findCodesTarget.querySelectorAll("button").forEach(b => b.addEventListener("click", () => { f.pkgId = b.dataset.id; f.sel = {}; this._renderFindCodes() }))
  }

  findSrcChanged(e) { e.currentTarget.closest(".srcrow")?.classList.toggle("off", !e.currentTarget.checked) }

  async runFind() {
    const f = this._find
    const srcs = this.findSrcTargets.filter(i => i.checked).map(i => i.dataset.src)
    const info = this._pkgInfo(f.pkgId)
    const lines = srcs.map(s => this._t(`line_src_${s}`, { division: info.divisionName || "" })).concat([this._t("line_rank")])
    this.findCodeLabelTarget.textContent = info.label
    this._findStep("busy")
    let idx = 0
    const paint = () => {
      this.findLinesTarget.innerHTML = lines.map((t, i) => {
        const cls = i < idx ? "done" : (i === idx ? "now" : "")
        const mark = i < idx ? `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m4.5 12.75 6 6 9-13.5"></path></svg>` : `<span class="dot"></span>`
        return `<div class="findline ${cls}">${mark}<span>${this._esc(t)}</span></div>`
      }).join("")
    }
    paint()
    if (this._findTimer) clearInterval(this._findTimer)
    this._findTimer = setInterval(() => { if (idx < lines.length - 1) { idx += 1; paint() } }, 350)
    const started = Date.now()
    try {
      const data = await this._json("GET", `${this.baseUrlValue}/bidders/find?package_id=${encodeURIComponent(f.pkgId)}&src=${srcs.join(",")}`)
      const wait = Math.max(0, Math.min(1400, lines.length * 350) - (Date.now() - started))
      await new Promise(r => setTimeout(r, wait))
      clearInterval(this._findTimer)
      idx = lines.length; paint()
      f.rows = data.rows || []
      f.sel = {}
      f.rows.forEach(r => { if (r.fit >= 85) f.sel[r.key] = true })
      f.strong = false
      this._renderFind()
      this._findStep("results")
    } catch (err) {
      clearInterval(this._findTimer)
      this._findStep("search")
      this._toast(err.message, "error")
    }
  }

  findAgain() { this._find.sel = {}; this._findStep("search") }

  findFilter(e) {
    this._find.strong = e.currentTarget.dataset.strong === "1"
    this._renderFind()
  }

  _fitCls(fit) { return fit >= 85 ? "hi" : (fit >= 70 ? "mid" : "lo") }

  _fitLabel(fit) { return this._t(fit >= 85 ? "fit_hi" : (fit >= 70 ? "fit_mid" : "fit_lo")) }

  _renderFind() {
    const f = this._find
    const info = this._pkgInfo(f.pkgId)
    const strongN = f.rows.filter(r => r.fit >= 85).length
    this.findFoundTarget.textContent = this._t("found", { count: f.rows.length, strong: strongN })
    this.findFilterTargets.forEach(b => b.classList.toggle("on", (b.dataset.strong === "1") === f.strong))
    const rows = f.rows.filter(r => !f.strong || r.fit >= 85)
    const chevron = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m8.25 4.5 7.5 7.5-7.5 7.5"></path></svg>`
    if (!rows.length) {
      this.findRowsTarget.innerHTML = `<div class="fempty"><span>${this._esc(f.rows.length ? this._t("strong_none") : this._t("found_none", { label: info.label }))}</span></div>`
    } else {
      this.findRowsTarget.innerHTML = rows.map(r => `
        <div class="t3 frow ${f.sel[r.key] ? "" : "dim"} ${f.detail === r.key ? "sel" : ""}" title="${this._esc(this.S.findRowTip || "")}" data-key="${this._esc(r.key)}">
          <label class="cb"><input type="checkbox" ${f.sel[r.key] ? "checked" : ""} aria-label="${this._esc(r.name)}"></label>
          <span><span class="strong block">${this._esc(r.name)}</span><span class="muted block">${this._esc(r.area || "")}</span><span class="muted block">${this._esc([r.contact, r.email || r.phone].filter(Boolean).join(" · "))}</span></span>
          <span class="sig">${(r.sigs || []).map(g => `<span class="badge ${g.cls}">${this._esc(g.text)}</span>`).join("")}<span class="sigmore">${this._esc(this.S.details || "")}${chevron}</span></span>
          <span>${this._esc(r.why || "")}</span>
          <span><span class="fit ${this._fitCls(r.fit)}"><span class="n">${r.fit}</span>${this._esc(this._fitLabel(r.fit))}</span></span>
        </div>`).join("")
      this.findRowsTarget.querySelectorAll(".frow").forEach(row => {
        const key = row.dataset.key
        const cb = row.querySelector("input")
        row.querySelector("label.cb").addEventListener("click", (ev) => ev.stopPropagation())
        cb.addEventListener("change", () => { f.sel[key] = cb.checked; this._renderFind() })
        row.addEventListener("click", () => { f.detail = f.detail === key ? null : key; this._renderFind() })
      })
    }
    const n = f.rows.filter(r => f.sel[r.key]).length
    const withPick = true
    this.findFootTarget.textContent = n ? this._t(withPick ? "foot_n" : "foot_n_list", { count: n, label: info.label }) : this._t("foot_none")
    this.findAddListTarget.disabled = !n
    this.findAddPickTarget.disabled = !n
    this.findAddPickTarget.textContent = n ? this._t("add_pick", { count: n }) : this._t("add_pick_empty")
    this._renderDetail()
  }

  _renderDetail() {
    const f = this._find
    const r = f && f.detail ? f.rows.find(x => x.key === f.detail) : null
    this.fpanelTarget.classList.toggle("hidden", !r)
    this.findModalTarget.classList.toggle("joined", !!r)
    if (!r) return
    const set = (k, v) => { const el = this.fpanelTarget.querySelector(`[data-fp='${k}']`); if (el) el.textContent = v || "—" }
    set("name", r.name); set("where", r.area); set("why", r.why)
    this.fpanelTarget.querySelector("[data-fp='fit']").innerHTML = `<span class="fit ${this._fitCls(r.fit)}"><span class="n">${r.fit}</span>${this._esc(this._fitLabel(r.fit))}</span>`
    set("person", r.contact); set("phone", r.phone); set("email", r.email); set("area", r.area)
    set("codes", (r.codes || []).join(", ")); set("hist", r.hist)
    set("ins", r.ins_label)
    set("license", r.license); set("w9", r.w9 ? "✓" : "—"); set("src", r.src_label)
    const notes = this.fpanelTarget.querySelector("[data-fp='notesSec']")
    notes.classList.toggle("hidden", !r.notes)
    set("notes", r.notes)
    const btn = this.fpanelTarget.querySelector("[data-fp='pick']")
    btn.textContent = f.sel[r.key] ? this._t("picked_one") : this._t("pick_one")
    btn.disabled = !!f.sel[r.key]
  }

  closeDetail() {
    if (this._find) this._find.detail = null
    if (this.hasFpanelTarget) this.fpanelTarget.classList.add("hidden")
    if (this.hasFindModalTarget) this.findModalTarget.classList.remove("joined")
  }

  detailPick() {
    const f = this._find
    if (!f?.detail) return
    f.sel[f.detail] = true
    this._renderFind()
  }

  async findAdd(e) {
    const f = this._find
    const pick = e.currentTarget.dataset.pick === "1"
    const keys = f.rows.filter(r => f.sel[r.key]).map(r => r.key)
    if (!keys.length) return
    const info = this._pkgInfo(f.pkgId)
    this.findAddListTarget.disabled = true
    this.findAddPickTarget.disabled = true
    try {
      const data = await this._json("POST", `${this.baseUrlValue}/bidders/find`, { package_id: f.pkgId, picks: keys, pick: pick ? "1" : "0" })
      this.closeFind()
      let msg = this._t(pick ? "added_toast_pick" : "added_toast_list", { count: data.added, label: info.label })
      if (data.no_email) msg += " " + this._t("no_email", { count: data.no_email })
      this._precon()?._applyResponse(data)
      this._toast(msg)
    } catch (err) {
      this._renderFind()
      this._toast(err.message, "error")
    }
  }

  // ── Import your sub list (the master sub list's spreadsheet import) ───
  openImport() {
    this._imp = null
    this.impChipTarget.classList.add("hidden")
    ;[this.impMapTtlTarget, this.impMapTarget, this.impNoteTarget].forEach(el => el.classList.add("hidden"))
    this.impBtnTarget.disabled = true
    this.impBtnTarget.textContent = this.S.impBtnEmpty || this.impBtnTarget.textContent
    this.impFileTarget.value = ""
    this.impModalTarget.classList.remove("hidden")
    track("Precon Sub Import Opened", { project_id: this.projectIdValue, from: "step 2" })
  }

  closeImport() { this.impModalTarget.classList.add("hidden") }

  impPick() { this.impFileTarget.click() }

  impOver(e) { e.preventDefault(); this.impDropTarget.style.outline = "2px solid #3b82f6" }

  impLeave() { this.impDropTarget.style.outline = "" }

  impDrop(e) {
    e.preventDefault()
    this.impLeave()
    const file = e.dataTransfer?.files?.[0]
    if (file) this._impUpload(file)
  }

  impFilePicked(e) { const file = e.target.files?.[0]; if (file) this._impUpload(file) }

  async _impUpload(file) {
    const chip = this.impChipTarget
    chip.querySelector("[data-f='name']").textContent = file.name
    chip.querySelector("[data-f='rows']").textContent = this._t("imp_reading")
    chip.classList.remove("hidden")
    ;[this.impMapTtlTarget, this.impMapTarget, this.impNoteTarget].forEach(el => el.classList.add("hidden"))
    this.impBtnTarget.disabled = true
    try {
      const pre = await this._json("POST", "/precon_subs/files/presign", { files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
      const up = pre.uploads[0]
      const put = await fetch(up.upload_url, { method: "PUT", body: file, headers: { "Content-Type": up.content_type || file.type || "application/octet-stream" } })
      if (!put.ok) throw new Error(this._t("imp_bad"))
      const data = await this._json("POST", "/precon_subs/import/preview", { cache_name: up.cache_name, filename: file.name })
      const p = data.preview || {}
      this._imp = { cache_name: up.cache_name, filename: file.name, found: p.found || 0 }
      chip.querySelector("[data-f='rows']").textContent = this._t("imp_rows", { count: p.rows || 0 })
      const arrow = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"></path></svg>`
      const to = this.S.js.imp_to || {}
      this.impMapTarget.innerHTML = (p.mapping || []).map(m => `<div class="maprow"><span>${this._esc(m.from)}</span>${arrow}<span class="strong">${this._esc(to[m.to] || m.to)}</span></div>`).join("")
      const note = [this._t("imp_found", { count: p.found || 0 })]
      if (p.dupes) note.push(this._t("imp_dupes", { count: p.dupes }))
      if (p.no_email) note.push(this._t("imp_no_email", { count: p.no_email }))
      this.impNoteTarget.querySelector("[data-f='text']").textContent = note.join(" ")
      ;[this.impMapTtlTarget, this.impMapTarget, this.impNoteTarget].forEach(el => el.classList.remove("hidden"))
      this.impBtnTarget.textContent = this._t("imp_btn", { count: p.found || 0 })
      this.impBtnTarget.disabled = !(p.found > 0)
    } catch (err) {
      chip.querySelector("[data-f='rows']").textContent = ""
      this._toast(err.message && err.message !== this._t("error") ? err.message : this._t("imp_bad"), "error")
    }
  }

  async doImport() {
    if (!this._imp) return
    this.impBtnTarget.disabled = true
    try {
      const data = await this._json("POST", "/precon_subs/import", { cache_name: this._imp.cache_name, filename: this._imp.filename })
      const r = data.result || {}
      this.closeImport()
      this._toast(this._t("imp_done", { count: (r.added || 0) + (r.updated || 0) }))
      this._precon()?._applyResponse({ stale: [2, 3] })
    } catch (err) {
      this.impBtnTarget.disabled = false
      this._toast(err.message, "error")
    }
  }

  // ── The split bar: drag, double-click to reset, arrow keys ────────────
  _applySavedWidth() {
    if (!this.hasListTarget) return
    let w = 340
    try { const v = parseInt(window.localStorage.getItem("conslog.precon.listWidth"), 10); if (v >= 220 && v <= 640) w = v } catch (e) { /* no storage */ }
    this._setWidth(w, false)
  }

  _setWidth(w, save = true) {
    const v = Math.round(Math.min(640, Math.max(220, w)))
    this.listTarget.style.width = `${v}px`
    if (this.hasBarTarget) this.barTarget.setAttribute("aria-valuenow", v)
    if (save) { try { window.localStorage.setItem("conslog.precon.listWidth", String(v)) } catch (e) { /* not saved */ } }
  }

  splitDown(e) {
    if (e.button !== undefined && e.button !== 0) return
    e.preventDefault()
    const box = this.barTarget.parentElement
    const maxW = Math.max(220, Math.min(640, Math.round(box.getBoundingClientRect().width * 0.6)))
    const startX = e.clientX
    const startW = this.listTarget.getBoundingClientRect().width
    this.barTarget.classList.add("drag")
    const move = (ev) => this._setWidth(Math.min(maxW, Math.max(220, startW + ev.clientX - startX)), false)
    const up = () => {
      window.removeEventListener("pointermove", move)
      window.removeEventListener("pointerup", up)
      window.removeEventListener("pointercancel", up)
      document.body.style.cursor = ""
      document.body.style.userSelect = ""
      this.barTarget.classList.remove("drag")
      this._setWidth(this.listTarget.getBoundingClientRect().width)
    }
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", up)
    window.addEventListener("pointercancel", up)
    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"
  }

  splitReset() { this._setWidth(340) }

  splitKey(e) {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return
    e.preventDefault()
    this._setWidth(this.listTarget.getBoundingClientRect().width + (e.key === "ArrowRight" ? 16 : -16))
  }
}
